import { useEffect, useRef, useState } from "react";
import { Avatar, Box, CircularProgress, Typography } from "@mui/material";
import ChatMessageRow from "./ChatMessageRow";
import CallActivityCard from "./CallActivityCard";
import { useChatContext } from "../hooks/ChatProvider";
import { WS_BASE_URL } from "../config/environment";

const getInitials = (name) => {
  if (!name || name === "Anonymous") return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

// The "All" tab's merged view for a dual-channel contact: one feed
// interleaving chat messages and calls, fetched over the same per-topic
// WebSocket ChatWindow.js uses, with mode=all added (see design.md
// Decision 3) — chat/consumers.py's ChatConsumer answers with
// activity.init/activity.page instead of messages.init/messages.page on
// that connection. Each item renders via the same ChatMessageRow/
// CallActivityCard components ChatWindow.js/CallHistoryWindow.js use
// (design.md Decision 4), so a dual-channel contact's merged view looks
// identical to its two single-channel views, just interleaved by time.
//
// Ordering matches ChatWindow.js's own convention (oldest-first, newest
// at the bottom, scroll-up-for-older), not the backend's own newest-first
// wire order: the backend/consumers.py send each page newest-first (so
// offset-based pagination is well-defined), and this component reverses
// each page before display and prepends older pages above the existing
// ones — the same trick ChatWindow.js already uses for messages.page.
//
// Only ever rendered by ContactDetailWindow.js when activeTab === "all"
// and the selected contact has both channels — selecting the same
// contact from "Chat" or "Calls" specifically still shows only that one
// channel via ChatWindow/CallHistoryWindow, unchanged.
const CombinedActivityWindow = () => {
  const { selectedUser } = useChatContext();
  const [items, setItems] = useState([]); // oldest -> newest, for display
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Only one call recording plays at a time across this feed — same
  // coordination CallHistoryWindow.js already uses (see
  // improve-call-recording-player's design.md Decision 1), lifted here
  // to cover a mixed chat+call feed instead of a pure call list.
  const [playingCallId, setPlayingCallId] = useState(null);
  const socketRef = useRef(null);
  const containerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const adminSubscriberCode = localStorage.getItem("code");
  const tenant = localStorage.getItem("tenant");

  useEffect(() => {
    if (!selectedUser?.topic) return;
    setItems([]);
    setLoading(true);
    setHasMore(false);
    setIsLoadingMore(false);

    const socketURL = `${WS_BASE_URL}/ws/chat/${selectedUser.topic}/?sub=${adminSubscriberCode}&tenant=${tenant}&mode=all`;
    const socket = new WebSocket(socketURL);
    socketRef.current = socket;

    socket.onmessage = (event) => {
      if (!event?.data) return;
      const data = JSON.parse(event.data);
      if (data.type === "activity.init") {
        // Newest-first off the wire -> oldest-first for display.
        setItems([...data.items].reverse());
        setHasMore(data.has_more);
        setLoading(false);
        return;
      }
      if (data.type === "activity.page") {
        // An older page — reverse it too, then prepend above what's
        // already shown (older content grows upward, like ChatWindow.js).
        const container = containerRef.current;
        prevScrollHeightRef.current = container ? container.scrollHeight : 0;
        setItems((prev) => [...[...data.items].reverse(), ...prev]);
        setHasMore(data.has_more);
        setIsLoadingMore(false);
      }
    };

    return () => socket.close();
  }, [selectedUser?.topic, adminSubscriberCode, tenant]);

  // Keep the view pinned to the bottom (newest) on first load and as new
  // items arrive while already at the bottom — same behavior
  // ChatWindow.js already has for messages.
  const prevItemCountRef = useRef(0);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const wasAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (wasAtBottom || prevItemCountRef.current === 0) {
      container.scrollTop = container.scrollHeight;
    }
    prevItemCountRef.current = items.length;
  }, [items]);

  // After an older page is prepended, restore the scroll position so the
  // content the user was looking at doesn't jump (ChatWindow.js's own
  // handleChatScroll technique).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !prevScrollHeightRef.current) return;
    container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = 0;
  }, [items]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el || !hasMore || isLoadingMore) return;
    if (el.scrollTop > 0) return; // load older only when scrolled to the top
    if (socketRef.current?.readyState !== WebSocket.OPEN) return;
    setIsLoadingMore(true);
    socketRef.current.send(
      JSON.stringify({ type: "activity.load_page", offset: items.length }),
    );
  };

  if (!selectedUser) return null;

  const displayName =
    selectedUser.name && selectedUser.name !== "Anonymous"
      ? selectedUser.name
      : `+${selectedUser.phone}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#ece5dd" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.75, bgcolor: "#ffffff", borderBottom: "1px solid #e8e8e8" }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: "#1565c0", fontSize: "0.9rem", fontWeight: 700 }}>
          {getInitials(selectedUser.name)}
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>{displayName}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>+{selectedUser.phone}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          ref={containerRef}
          onScroll={handleScroll}
          sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}
        >
          {isLoadingMore && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          )}
          {items.map((item) =>
            item.type === "call" ? (
              <CallActivityCard
                key={`call-${item.id}`}
                call={item}
                activeCallId={playingCallId}
                onPlay={setPlayingCallId}
              />
            ) : (
              <ChatMessageRow key={`chat-${item.id}`} msg={item} />
            ),
          )}
          {items.length === 0 && (
            <Typography sx={{ fontSize: "0.85rem", color: "#aaa", textAlign: "center", mt: 4 }}>
              No activity yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CombinedActivityWindow;
